/* eslint-disable jsx-a11y/no-access-key */
import { FormEvent, useCallback, useMemo, useState } from 'react'

import { SubdirectoryArrowRight } from '@mui/icons-material'
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Input,
  InputLabel,
  TextField,
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Button, useGetList, useNotify, useTranslate } from 'react-admin'
import { API } from '../../helpers/Api.helper'
import { Artwork } from '../../types/Artwork'
import { ICreatePrepayment, PrepaymentTargetType } from '../../types/Prepayments'
import { Product } from '../../types/Product'

import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { DatePickerSX } from '../LXSelectDate'
import styles from './Prepayments.module.scss'

type PrepaymentForm = {
  label: string
  amountInEuros: string
  recognitionPeriod: Dayjs | undefined
}

type CreatePrepaymentProps = {
  targetType: PrepaymentTargetType
  targetId: string
  isModalOpen: boolean
  setIsModalOpen: (b: boolean) => void
  onSuccess?: () => void
}

export const CreatePrepayment = ({
  targetType,
  targetId,
  isModalOpen,
  setIsModalOpen,
  onSuccess,
}: CreatePrepaymentProps) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [formData, setFormData] = useState<PrepaymentForm>({
    label: '',
    amountInEuros: '0',
    recognitionPeriod: undefined,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>()
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | undefined>()

  const createPrepaymentMutation = useMutation({
    mutationKey: ['createPrepayment', targetType, targetId, formData],
    mutationFn: (body: ICreatePrepayment) => {
      return API.post('/api/prepayments', body)
    },
  })

  const productsQuery = useGetList(
    'products',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        beneficiaryId: targetId,
      },
    },
    {
      enabled: targetType === 'beneficiary',
    },
  )

  const artworksQuery = useGetList(
    'artworks',
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {
        ...(targetType === 'beneficiary' ? { productId: selectedProduct?.id } : {}),
        ...(targetType === 'product' ? { productId: targetId } : {}),
      },
    },
    {
      enabled: (targetType === 'beneficiary' && !!selectedProduct) || targetType === 'product',
    },
  )

  const onCreate = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      try {
        setIsLoading(true)

        const { label, amountInEuros, recognitionPeriod } = formData
        const { id, type } = (() => {
          if (selectedArtwork) {
            return {
              id: selectedArtwork.id,
              type: 'artwork' as PrepaymentTargetType,
            }
          }
          if (selectedProduct) {
            return { id: selectedProduct.id, type: 'product' as PrepaymentTargetType }
          }

          return {
            id: targetId,
            type: targetType,
          }
        })()
        await createPrepaymentMutation.mutateAsync({
          targetType: type,
          targetId: id,
          label,
          amountInEuros,
          ...(recognitionPeriod
            ? { recognitionPeriod: recognitionPeriod.toDate().toISOString() }
            : undefined),
        })

        notify(translate('pages.prepayments.addPrepaymentModal.success'), { type: 'success' })

        onSuccess?.()
        setIsLoading(false)
        setIsModalOpen(false)
        setFormData({
          label: '',
          amountInEuros: '0',
          recognitionPeriod: undefined,
        })
        setSelectedArtwork(undefined)
        setSelectedProduct(undefined)
      } catch (e) {
        console.error(e)

        const message =
          (e as AxiosError)?.response?.data.message || (e as AxiosError)?.response?.data || e
        notify(`${translate('pages.prepayments.addPrepaymentModal.error')} - ${message}`, {
          type: 'error',
        })
        setIsLoading(false)
      }
    },
    [
      targetType,
      targetId,
      formData,
      onSuccess,
      setIsModalOpen,
      notify,
      translate,
      selectedProduct,
      selectedArtwork,
    ],
  )

  const onChange = (target: any) => {
    const { name, value } = target
    setFormData({ ...formData, [name]: value })
  }

  const artworksSelect = useMemo(
    () => (
      <Autocomplete
        id="prepayment-artworks"
        className={styles.selectArtwork}
        options={artworksQuery?.data ?? []}
        value={selectedArtwork}
        accessKey="id"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={option => option.name}
        renderInput={params => (
          <TextField
            {...params}
            key={params.id}
            placeholder={translate('_generics.artwork')}
            variant="standard"
          />
        )}
        noOptionsText={translate('_generics.noResult')}
        onChange={(_, value) => setSelectedArtwork(value !== null ? value : undefined)}
      />
    ),
    [artworksQuery?.data, selectedArtwork, translate],
  )

  return (
    <Dialog
      aria-labelledby="create-prepayment"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      disableEscapeKeyDown
    >
      <form onSubmit={onCreate}>
        <DialogTitle id="create-prepayment">
          {translate('pages.prepayments.addPrepaymentModal.title')}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container direction="row" spacing={4}>
            <Grid item>
              <FormControl>
                <InputLabel htmlFor="label">{translate('_generics.label')}</InputLabel>
                <Input
                  id="label"
                  name="label"
                  aria-describedby="label"
                  value={formData.label}
                  onChange={e => onChange(e.currentTarget)}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl>
                <InputLabel htmlFor="amountInEuros">
                  {translate('pages.prepayments.amountInEuros')}
                </InputLabel>
                <Input
                  id="amountInEuros"
                  name="amountInEuros"
                  type="number"
                  inputProps={{
                    min: 0,
                    step: 0.1,
                  }}
                  aria-describedby="amountInEuros"
                  value={formData.amountInEuros}
                  onChange={e => onChange(e.currentTarget)}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container direction="column" spacing={4} mt={0.1}>
            {targetType === 'beneficiary' && (
              <>
                <Grid item>
                  <Autocomplete
                    id="prepayment-products"
                    options={productsQuery?.data ?? []}
                    value={selectedProduct}
                    accessKey="id"
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={option => option.name}
                    renderInput={params => (
                      <TextField
                        {...params}
                        key={params.id}
                        placeholder={translate('_generics.product')}
                        variant="standard"
                      />
                    )}
                    noOptionsText={translate('_generics.noResult')}
                    onChange={(_, value) => setSelectedProduct(value !== null ? value : undefined)}
                  />
                </Grid>
                {typeof selectedProduct !== 'undefined' && (
                  <Grid item mt={-3}>
                    <div className={styles.artworkWrapper}>
                      <SubdirectoryArrowRight />
                      {artworksSelect}
                    </div>
                  </Grid>
                )}
              </>
            )}
            {targetType === 'product' && (
              <Grid item mt={-3}>
                {artworksSelect}
              </Grid>
            )}
          </Grid>
          <Grid container direction="column" spacing={4}>
            <Grid item marginTop={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  // label={translate('_generics.period')}
                  value={formData.recognitionPeriod}
                  defaultValue={undefined}
                  onAccept={value => {
                    onChange({
                      name: 'recognitionPeriod',
                      value,
                    })
                  }}
                  slotProps={{
                    toolbar: { hidden: true },
                  }}
                  views={['month', 'year']}
                  sx={{
                    '& input': {
                      backgroundColor: 'white',
                      paddingLeft: 0,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false)
            }}
            label={translate('_generics.cancel')}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            label={translate('_generics.create')}
            disabled={
              isLoading || formData.label.length === 0 || formData.amountInEuros.length === 0
            }
          />
        </DialogActions>
      </form>
    </Dialog>
  )
}
